/**
 * Diagnostic Lead Artisan™ — Question definitions with conditional logic
 * Each step has an id, title, questions, and optional `condition` function.
 */

export const METIERS = [
  "Plombier", "Sanitaire", "Chauffagiste", "Électricien", "Serrurier",
  "Menuisier", "Peintre", "Plâtrier", "Carreleur", "Maçon",
  "Façadier", "Couvreur", "Ferblantier", "Vitrier", "Paysagiste",
  "Photovoltaïque", "Rénovation", "Autre",
];

export const CANTONS = [
  "Vaud", "Genève", "Fribourg", "Valais", "Neuchâtel", "Jura", "Berne francophone", "Autre",
];

// Steps definition
export const STEPS = [
  // ─── STEP 1: Profil ───
  {
    id: "profil",
    title: "Votre entreprise",
    subtitle: "Quelques informations pour personnaliser votre diagnostic.",
    questions: [
      { id: "metier", label: "Quel est votre métier principal ?", type: "select", options: METIERS, required: true },
      { id: "canton", label: "Dans quel canton êtes-vous basé ?", type: "pills", options: CANTONS, required: true },
      { id: "npa", label: "Votre NPA", type: "text", placeholder: "Ex: 1000", required: true },
      { id: "rayon", label: "Dans quel rayon intervenez-vous ?", type: "pills", options: [
        { value: "moins_10km", label: "< 10 km" },
        { value: "10_20km", label: "10–20 km" },
        { value: "20_30km", label: "20–30 km" },
        { value: "30_50km", label: "30–50 km" },
        { value: "plus_50km", label: "+ 50 km" },
      ]},
      { id: "taille", label: "Combien de personnes dans votre entreprise ?", type: "pills", options: [
        { value: "seul", label: "Seul" },
        { value: "2_3", label: "2–3" },
        { value: "4_10", label: "4–10" },
        { value: "11_20", label: "11–20" },
        { value: "plus_20", label: "20+" },
      ]},
      { id: "anciennete", label: "Depuis combien de temps votre entreprise existe-t-elle ?", type: "pills", options: [
        { value: "moins_1an", label: "< 1 an" },
        { value: "1_3ans", label: "1–3 ans" },
        { value: "3_5ans", label: "3–5 ans" },
        { value: "5_10ans", label: "5–10 ans" },
        { value: "plus_10ans", label: "10+ ans" },
      ]},
    ],
  },

  // ─── STEP 2: Carnet de commandes ───
  {
    id: "carnet",
    title: "Votre carnet de commandes",
    subtitle: "Comment se porte votre activité aujourd'hui ?",
    questions: [
      { id: "situation", label: "Quelle situation décrit le mieux votre entreprise ?", type: "cards", options: [
        { value: "besoin_clients", label: "J'ai clairement besoin de plus de clients" },
        { value: "irreguliere", label: "Mon activité est irrégulière" },
        { value: "suffisant", label: "J'ai suffisamment de travail actuellement" },
        { value: "mauvais_chantiers", label: "J'ai du travail mais pas toujours les bons chantiers" },
        { value: "deborde", label: "Je suis débordé et j'ai du mal à gérer" },
        { value: "developper", label: "Je veux développer davantage mon entreprise" },
      ]},
      { id: "planning_rempli", label: "Jusqu'à quand votre planning est-il correctement rempli ?", type: "pills", options: [
        { value: "moins_2_semaines", label: "< 2 semaines" },
        { value: "2_4_semaines", label: "2–4 sem." },
        { value: "1_2_mois", label: "1–2 mois" },
        { value: "2_3_mois", label: "2–3 mois" },
        { value: "plus_3_mois", label: "3+ mois" },
        { value: "variable", label: "Très variable" },
      ]},
      { id: "previsibilite_3mois", label: "Si rien ne change, savez-vous combien de nouvelles demandes vous recevrez dans 3 mois ?", type: "cards", options: [
        { value: "oui", label: "Oui, clairement" },
        { value: "plus_ou_moins", label: "Plus ou moins" },
        { value: "non", label: "Non, pas vraiment" },
        { value: "aucune_idee", label: "Absolument aucune idée" },
      ]},
    ],
  },

  // ─── STEP 3: Origine clients ───
  {
    id: "sources",
    title: "L'origine de vos clients",
    subtitle: "D'où viennent vos nouveaux clients aujourd'hui ?",
    questions: [
      { id: "sources_clients", label: "D'où viennent vos nouveaux clients ?", type: "multi", options: [
        "Bouche-à-oreille", "Anciens clients", "Google", "Google Maps",
        "Site internet", "Facebook", "Instagram", "Publicité Meta", "Google Ads",
        "Plateformes de mise en relation", "Architectes", "Régies",
        "Entreprises partenaires", "Prospection", "Autre",
      ]},
      { id: "bouche_a_oreille_pct", label: "Quelle proportion de vos clients vient du bouche-à-oreille ?", type: "pills", options: [
        { value: "0_25", label: "0–25 %" },
        { value: "25_50", label: "25–50 %" },
        { value: "50_75", label: "50–75 %" },
        { value: "75_100", label: "75–100 %" },
      ]},
      { id: "source_backup", label: "Si votre source principale s'arrêtait demain, auriez-vous un plan B ?", type: "cards", options: [
        { value: "oui", label: "Oui, j'ai d'autres sources fiables" },
        { value: "partiellement", label: "Partiellement" },
        { value: "non", label: "Non, ce serait problématique" },
      ]},
    ],
  },

  // ─── STEP 4: Volume ───
  {
    id: "volume",
    title: "Volume de demandes",
    subtitle: "Quantifions votre activité commerciale.",
    questions: [
      { id: "demandes_mois", label: "Nouvelles demandes reçues par mois ?", type: "number", placeholder: "Ex: 15" },
      { id: "demandes_objectif", label: "Combien souhaiteriez-vous en recevoir ?", type: "number", placeholder: "Ex: 25" },
      { id: "clients_mois", label: "Nouveaux clients signés par mois ?", type: "number", placeholder: "Ex: 7" },
      { id: "clients_objectif", label: "Combien souhaiteriez-vous en signer ?", type: "number", placeholder: "Ex: 12" },
    ],
  },

  // ─── STEP 5: Qualité prospects ───
  {
    id: "qualite",
    title: "Qualité des prospects",
    subtitle: "Vos demandes sont-elles réellement exploitables ?",
    questions: [
      { id: "qualite_prospects", label: "Parmi vos demandes, combien sont réellement intéressantes ?", type: "pills", options: [
        { value: "moins_25", label: "< 25 %" },
        { value: "25_50", label: "25–50 %" },
        { value: "50_75", label: "50–75 %" },
        { value: "plus_75", label: "75 %+" },
      ]},
      { id: "problemes_prospects", label: "Quels problèmes rencontrez-vous ?", type: "multi", options: [
        "Prospect impossible à joindre", "Demande hors secteur", "Budget insuffisant",
        "Client cherchant uniquement un prix", "Demande ne correspondant pas à mes prestations",
        "Client demandant plusieurs devis sans projet réel", "Rendez-vous annulés",
        "Aucun problème particulier",
      ]},
      { id: "deplacement_inutile", label: "Vous arrive-t-il de vous déplacer pour un prospect non qualifié ?", type: "cards", options: [
        { value: "souvent", label: "Souvent" },
        { value: "parfois", label: "Parfois" },
        { value: "rarement", label: "Rarement" },
        { value: "jamais", label: "Jamais" },
      ]},
    ],
  },

  // ─── STEP 6: Devis ───
  {
    id: "devis",
    title: "Vos devis",
    subtitle: "Comment se passe votre processus de devis ?",
    questions: [
      { id: "devis_mois", label: "Devis réalisés par mois ?", type: "number", placeholder: "Ex: 12" },
      { id: "devis_signes", label: "Devis signés par mois ?", type: "number", placeholder: "Ex: 4" },
      { id: "temps_devis", label: "Temps moyen pour un devis (visite + rédaction + envoi) ?", type: "pills", options: [
        { value: "moins_30min", label: "< 30 min" },
        { value: "30_60min", label: "30–60 min" },
        { value: "1_2h", label: "1–2h" },
        { value: "2_3h", label: "2–3h" },
        { value: "plus_3h", label: "3h+" },
      ]},
      { id: "relance_devis", label: "Relancez-vous vos devis non signés ?", type: "pills", options: [
        { value: "toujours", label: "Toujours" },
        { value: "souvent", label: "Souvent" },
        { value: "parfois", label: "Parfois" },
        { value: "rarement", label: "Rarement" },
        { value: "jamais", label: "Jamais" },
      ]},
      { id: "nombre_relances", label: "Combien de relances en moyenne ?", type: "pills", options: [
        { value: "0", label: "0" },
        { value: "1", label: "1" },
        { value: "2", label: "2" },
        { value: "3+", label: "3+" },
      ]},
    ],
  },

  // ─── STEP 7: Appels ───
  {
    id: "appels",
    title: "Appels et réactivité",
    subtitle: "Que se passe-t-il quand un prospect vous appelle ?",
    questions: [
      { id: "reponse_chantier", label: "Sur chantier, pouvez-vous répondre aux prospects ?", type: "pills", options: [
        { value: "toujours", label: "Toujours" },
        { value: "souvent", label: "Souvent" },
        { value: "parfois", label: "Parfois" },
        { value: "rarement", label: "Rarement" },
        { value: "jamais", label: "Jamais" },
      ]},
      { id: "appels_manques_semaine", label: "Appels manqués par semaine ?", type: "pills", options: [
        { value: "0", label: "0" },
        { value: "1_3", label: "1–3" },
        { value: "4_10", label: "4–10" },
        { value: "10+", label: "10+" },
      ]},
      { id: "delai_rappel", label: "En combien de temps rappelez-vous un prospect ?", type: "pills", options: [
        { value: "moins_5min", label: "< 5 min" },
        { value: "moins_30min", label: "< 30 min" },
        { value: "quelques_heures", label: "Quelques h" },
        { value: "fin_journee", label: "Fin de journée" },
        { value: "lendemain", label: "Lendemain" },
        { value: "variable", label: "Variable" },
      ]},
      { id: "appels_soir_weekend", label: "Que se passe-t-il le soir ou le week-end ?", type: "cards", options: [
        { value: "quelquun_repond", label: "Quelqu'un répond" },
        { value: "repondeur", label: "Répondeur" },
        { value: "rappel_ulterieur", label: "Rappel ultérieur" },
        { value: "rien", label: "Rien de systématique" },
      ]},
    ],
  },

  // ─── STEP 8: Outils digitaux ───
  {
    id: "outils",
    title: "Vos outils",
    subtitle: "De quels outils disposez-vous aujourd'hui ?",
    questions: [
      { id: "outils_digitaux", label: "Cochez tout ce que vous avez :", type: "multi", options: [
        { value: "site_internet", label: "Site internet" },
        { value: "google_business", label: "Google Business Profile" },
        { value: "crm", label: "CRM / logiciel de gestion" },
        { value: "devis_systeme", label: "Système de devis" },
        { value: "relances_auto", label: "Relances automatiques" },
        { value: "sms_auto", label: "SMS automatiques" },
        { value: "whatsapp_business", label: "WhatsApp Business" },
        { value: "rdv_en_ligne", label: "Prise de RDV en ligne" },
        { value: "standard_tel", label: "Standard téléphonique" },
        { value: "assistant_vocal", label: "Assistant vocal / IA" },
        { value: "suivi_leads", label: "Suivi des leads" },
      ]},
    ],
  },

  // ─── STEP 9: Économie ───
  {
    id: "economie",
    title: "Chiffres clés",
    subtitle: "Encore quelques données pour estimer votre potentiel.",
    questions: [
      { id: "panier_moyen", label: "Panier moyen d'un nouveau chantier/client ?", type: "pills", options: [
        { value: "moins_500", label: "< 500 CHF" },
        { value: "500_1500", label: "500–1'500" },
        { value: "1500_3000", label: "1'500–3'000" },
        { value: "3000_5000", label: "3'000–5'000" },
        { value: "5000_10000", label: "5'000–10'000" },
        { value: "10000_25000", label: "10'000–25'000" },
        { value: "25000+", label: "25'000+" },
      ]},
      { id: "connait_marge", label: "Connaissez-vous votre marge moyenne ?", type: "pills", options: [
        { value: "oui", label: "Oui" },
        { value: "non", label: "Non" },
      ]},
      { id: "marge_pct", label: "Votre marge approximative ?", type: "number", placeholder: "Ex: 35", condition: (a) => a.connait_marge === "oui" },
    ],
  },

  // ─── STEP 10: Objectifs ───
  {
    id: "objectifs",
    title: "Votre priorité",
    subtitle: "Dernière étape avant votre diagnostic.",
    questions: [
      { id: "priorite", label: "Quelle est votre priorité n°1 aujourd'hui ?", type: "cards", options: [
        { value: "plus_clients", label: "Trouver plus de clients" },
        { value: "meilleurs_clients", label: "Trouver de meilleurs clients" },
        { value: "remplir_planning", label: "Remplir mon planning" },
        { value: "augmenter_ca", label: "Augmenter mon chiffre d'affaires" },
        { value: "rentabilite", label: "Améliorer ma rentabilité" },
        { value: "reduire_temps", label: "Réduire le temps perdu" },
        { value: "automatiser", label: "Automatiser les relances" },
        { value: "plus_appels", label: "Ne plus manquer d'appels" },
        { value: "taux_signature", label: "Améliorer mon taux de signature" },
        { value: "previsibilite", label: "Rendre mon activité prévisible" },
        { value: "developper", label: "Développer mon entreprise" },
      ]},
      { id: "timing", label: "Quand souhaitez-vous améliorer cette situation ?", type: "pills", options: [
        { value: "maintenant", label: "Maintenant" },
        { value: "30_jours", label: "Dans 30 jours" },
        { value: "3_mois", label: "Dans 3 mois" },
        { value: "plus_tard", label: "Plus tard" },
        { value: "juste_savoir", label: "Je veux juste savoir" },
      ]},
    ],
  },
];

export const TOTAL_STEPS = STEPS.length + 2; // +2 for analysis + contact
